/// FOURJS_START_COPYRIGHT(D,2025)
/// Property of Four Js*
/// (c) Copyright Four Js 2025, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT
"use strict";

import intlTelInput from "intl-tel-input/intlTelInputWithUtils";

modulum("PhoneEditWidget", ["EditWidget", "WidgetFactory"], function (context, cls) {
  /**
   * @class PhoneEditWidget
   * @memberOf classes
   * @extends classes.EditWidget
   */
  cls.PhoneEditWidget = context.oo.Class(cls.EditWidget, function ($super) {
    /** @lends classes.PhoneEditWidget.prototype */
    return {
      __name: "PhoneEditWidget",
      __templateName: "PhoneEditWidget",

      /**
       * The object that manages the phone input features
       */
      _iti: null,

      /**
       * The element containing the dropdown arrow
       * @type {HTMLElement}
       */
      _arrowElement: null,

      _initialCountry: "auto", // auto: based on user's IP address

      /**
       * @inheritDoc
       */
      constructor: function (opts) {
        $super.constructor.call(this, opts);
      },

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        $super._initLayout.call(this);
        if (!this._ignoreLayout) {
          this._layoutInformation = new cls.LayoutInformation(this);
          this._layoutEngine = new cls.EditLayoutEngine(this);
          // Set negative decoration as the flag and code are inside the input
          // Parameter: space taken by the country flag
          this._layoutInformation.setReservedDecorationSpace(-2);
        }
      },

      /**
       * @inheritDoc
       */
      _afterInitElement: function () {
        $super._afterInitElement.call(this);
        this._initTelInput();
        this._arrowElement = this.getElement().getElementsByClassName("iti__arrow")[0];
      },

      /**
       * Provides all options to the phone widget
       * @private
       */
      _initTelInput: function () {
        const tel = intlTelInput;
        const input = this.getInputElement();
        this._iti = tel(input, {
          initialCountry: this._initialCountry,
          // IP lookup is done if initialCountry is auto, and it is done only once for all phone widgets
          geoIpLookup: (success, failure) => {
            fetch("https://ipapi.co/json")
              .then((res) => res.json())
              .then((data) => {
                success(data.country_code);
              })
              .catch(() => failure());
          },
          strictMode: true,
          dropdownContainer: document.body,
          fixDropdownWidth: false,
          customPlaceholder: (selectedCountryPlaceholder, selectedCountryData) => {
            return selectedCountryPlaceholder.replaceAll(/\d/g, "0");
          }
        });

        this._iti.promise
          .then(() => {
            // When auto flag gets resolved
            if (this._initialCountry === "auto" && input.value && !this._iti.isValidNumber()) {
              this._iti.setCountry(); // Remove the auto flag if initial value was not a valid number
            }
          })
          .catch(() => {
            // intl-tel-input rejects its internal `autoCountryPromise` (with no reason)
            // whenever the geoIpLookup callback above fails — e.g. ipapi.co returns a
            // 403 without CORS headers from localhost. The widget then falls back to
            // the default country on its own; the rejection carries no actionable
            // information and would otherwise surface as `Uncaught (in promise) undefined`.
          });
      },

      /**
       * @inheritDoc
       */
      setFocus: function (fromMouse = false, preventScroll = false) {
        $super.setFocus.call(this, fromMouse, preventScroll);
        if (this._isDropdownOpen()) {
          this._iti.searchInput.focus();
        }
      },

      /**
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        let keyProcessed = false;
        if (this._isDropdownOpen()) {
          switch (keyString) {
            case "enter":
            case "return":
            case "esc":
            case "up":
            case "down": // up/down bypass needed in input array
              keyProcessed = true;
              // we flag key processed as true to not let enter key (for example) fire an 'accept' action
              // but since the keys are managed by the library being located close to body in the DOM, we need the dom event to bubble to it
              // to do that, we have to disable the stopPropagation being done by default on all events flagged with keyProcessed=true
              domKeyEvent.allowPropagation = true;
              break;
          }
        }

        if (keyProcessed) {
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * @inheritDoc
       */
      manageInput: function (dataString = "", event = null) {
        if (event?.detail?.isSetNumber) {
          // Ignore if caused by setNumber (used for formatting)
          return;
        }
        $super.manageInput.call(this, dataString, event);
        // If pasting a valid number, format it directly
        if (this._iti._utilsIsPossibleNumber(dataString)) {
          this._iti.setNumber(dataString);
        }
      },

      /**
       * @inheritDoc
       */
      getValueForClipboard: function (ignoreSelection = false) {
        const selectedText = $super.getValueForClipboard.call(this, ignoreSelection);
        // If the entire input is selected, format the value
        if (selectedText === this.getInputElement().value) {
          return this.getValue();
        }
        return selectedText;
      },

      /**
       * @inheritDoc
       */
      setCursors: function (start, end) {
        // Not supported, except when end is negative to select all on tab
        if (end < 0) {
          $super.setCursors.call(this, start, end);
        }
      },

      /**
       * @inheritDoc
       */
      getCursors: function () {
        // Not supported
        return { start: 0, end: 0 };
      },

      /**
       * @inheritDoc
       */
      getValue: function () {
        if (this._iti.isValidNumber()) {
          return this._iti.getNumber(); // Value formatted to E.164 standard with country code
        }
        return this.getInputElement().value; // Raw value
      },

      /**
       * @inheritDoc
       */
      setValue: function (value, fromVM = false, cursorPosition = null) {
        $super.setValue.call(this, value, fromVM, cursorPosition);

        if (!value) {
          return; // To keep the selected country when set to empty
        }
        if (this._iti._utilsIsPossibleNumber(value)) {
          this._iti.setNumber(value); // Format number received from the VM
        } else {
          // Set to undefined country
          // Using this method to avoid undesired side effect
          this._iti._setCountry();
        }
      },

      /**
       * @inheritDoc
       */
      setPlaceHolder: function (placeholder, fake) {
        // Empty override to bypass VM placeholder behavior
      },

      /**
       * Checks if the country list dropdown is open (it is not a DropdownWidget)
       * @returns {boolean}
       * @private
       */
      _isDropdownOpen: function () {
        return !!document.getElementsByClassName("iti__dropdown-content")[0];
      },

      /**
       * @inheritDoc
       */
      setEnabled: function (enabled) {
        $super.setEnabled.call(this, enabled);
        this._iti.setDisabled(!enabled);
        cls.DOMElementHelper.toggleClasses(this._arrowElement, !this._enabled, "iti__hide");
      },

      /**
       * @inheritDoc
       */
      setReadOnly: function (readonly) {
        $super.setReadOnly.call(this, readonly);
        this._iti.setDisabled(readonly);
        cls.DOMElementHelper.toggleClasses(this._arrowElement, readonly, "iti__hide");
      },

      triggerValueChangedEvent: function (newValue, sendValue = true) {
        if (sendValue === false || newValue?.toString() !== this._oldValue) {
          this.emit(context.constants.widgetEvents.valueChanged, newValue, sendValue);
        }
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        $super.destroy.call(this);
        this._iti.destroy();
      }
    };
  });

  cls.WidgetFactory.registerBuilder("PhoneEdit", cls.PhoneEditWidget);
  cls.WidgetFactory.registerBuilder("Edit[customWidget=phoneEdit]", cls.PhoneEditWidget);
  cls.WidgetFactory.registerBuilder("Label[customWidget=phoneEdit]", cls.PhoneEditWidget);
});
